import { Suspense, useCallback, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import useWebsocket, { ReadyState } from 'react-use-websocket';

import LoadingIndicator from '../components/common/LoadingIndicator';
import Sidebar from '../features/app/components/Sidebar';
import { createSocketConnection, selectSocketState } from '../store/socket';
import { useAppDispatch, useAppSelector } from '../store';
import { logoutUserThunk } from '../features/auth/api/authSlice';
import usePrevious from '../hooks/usePrevious';
import SessionManagementProvider from '../providers/SessionManagementProvider';
import { createNotificationActionMessage, NotificationTypeSocketCommand } from '../utils/socket';
import { selectAccountState, user } from '../features/account/api/accountSlice';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import useIsMobile from '../hooks/useIsMobile';

export function RouteErrorFallback() {
  const { t }: any = useTranslation();
  return (
    <div className='d-flex flex-column min-vh-100 min-vw-100'>
      <div className='d-flex flex-grow-1 justify-content-center align-items-center'>
        <div className='text-center'>
          <h1>{t('Something went wrong.')}</h1>
          <button className='text-white btn btn-danger' onClick={() => window.location.reload()}>
            Refresh the page
          </button>
        </div>
      </div>
    </div>
  );
}

export const AppLayout = () => {
  const dispatch = useAppDispatch();
  const { connectionUrl } = useAppSelector(selectSocketState);

  const patient = useAppSelector(user);

  const getSocketUrl = useCallback(async () => {
    await dispatch(createSocketConnection()).unwrap();
  }, [dispatch]);

  useEffect(() => {
    getSocketUrl();
  }, [getSocketUrl]);

  const { sendMessage, readyState } = useWebsocket(connectionUrl, {
    onOpen: () => console.log('opened'),
    share: true,
  });

  const logout = useCallback(() => dispatch(logoutUserThunk()), [dispatch]);

  // * TEMP. ZOOM LEFTOVERS WORKAROUND
  const location = useLocation();
  const previousLocation = usePrevious(location);
  const { sidebarMenuCollapsed } = useAppSelector(selectAccountState);

  useEffect(() => {
    if (previousLocation?.pathname.includes('meeting') && !location.pathname.includes('meeting')) {
      console.log('Leaving meeting route', location, previousLocation);
      window.location.reload();
    }
  }, [location, previousLocation]);

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(
        JSON.stringify(
          createNotificationActionMessage(NotificationTypeSocketCommand.VISIT, patient?.id),
        ),
      );
    }
  }, [patient?.id, readyState, sendMessage]);

  const isMobile = useIsMobile();

  return (
    <>
      <div className={`container-fluid`}>
        <div
          className={`row ${isMobile ? 'h-100' : 'vh-100'}`}
          id='page-list'
          style={{ overflow: 'auto' }}
        >
          <div className='col-12 p-0 col-md-auto app__sidebar-wrap'>
            <Sidebar />
          </div>
          <div className='col p-0 d-flex flex-column'>
            <main className={`flex-grow-1 app__main${sidebarMenuCollapsed ? '-collapsed' : ''}`}>
              <Suspense fallback={<LoadingIndicator />}>
                <SessionManagementProvider
                  onNetworkLost={logout}
                  onNetworkLostRegainThresholdSeconds={30}
                >
                  <ErrorBoundary FallbackComponent={RouteErrorFallback}>
                    <Outlet />
                  </ErrorBoundary>
                </SessionManagementProvider>
              </Suspense>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};
